<template>
  <div :class="containerClasses">
    <component v-if="hero && hero.module" v-bind="hero" :is="SUPPORTED_COMPONENTS[hero.module]">
      <template v-for="(component, i) in hero.components || []">
        <component
          v-if="component && component.module"
          v-on="component.listeners || {}"
          v-bind="component"
          :key="`${component._uid}_${i}`"
          :is="SUPPORTED_COMPONENTS[component.module]"
          slot="content"
        >
          <span v-for="(slot, key) in component.slots || {}" :key="key" :slot="key" v-sanitize-html="slot" />
        </component>
      </template>
    </component>
    <section class="flex-layout__content">
      <spinner v-if="computedIsLoading" />
      <storyblok-layout-row
        v-else
        v-for="(row, rowIndex) in computedRows"
        v-bind="row"
        v-editable="row"
        :key="row._uid || rowIndex"
      >
        <storyblok-section
          v-for="(section, sectionIndex) in row.sections"
          v-bind="section"
          v-editable="section"
          :key="section._uid || sectionIndex"
        >
          <storyblok-section-column
            v-for="(column, columnIndex) in section.columns"
            v-bind="column"
            v-editable="column"
            :key="column._uid || columnIndex"
          >
            <template v-for="(component, componentIndex) in column.components" :key="componentIndex">
              <component
                :key="component._uid || componentIndex"
                :is="SUPPORTED_COMPONENTS[component.module]"
                v-if="component && component.module"
                v-bind="component"
                v-on="component.listeners || {}"
                v-editable="component"
              >
                <span
                  v-for="(slot, key) in component.slots || {}"
                  :slot="key"
                  :key="`${component._uid}_${key}`"
                  v-sanitize-html="slot"
                />
              </component>
            </template>
          </storyblok-section-column>
        </storyblok-section>
      </storyblok-layout-row>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue';

const BASE_CLASS = 'flex-layout';

const NATIVE_COMPONENTS = {
  a: 'a',
  p: 'p'
};

const StoryblokSection = defineAsyncComponent(() => import('@/components/atoms/SSection.vue'));
const StoryblokLayoutRow = defineAsyncComponent(() => import('@/components/atoms/SLayoutRow.vue'));
const StoryblokSectionColumn = defineAsyncComponent(() => import('@/components/atoms/SSectionColumn.vue'));
const Spinner = defineAsyncComponent(() => import('@/components/atoms/Spinner.vue'));

const props = defineProps({
  hero: {
    type: Object,
    required: false,
    default: null
  },
  rows: {
    type: [Array, Object],
    required: false,
    default: () => []
  },
  onSetup: {
    type: Function,
    default: () => {}
  },
  isLoading: {
    type: [Boolean, Object],
    default: false
  }
});
if (props.onSetup) props.onSetup();

const computedRows = computed(() => (Array.isArray(props.rows) ? props.rows : props.rows.value));
const computedIsLoading = computed(() =>
  props.isLoading.value !== undefined ? props.isLoading.value === true : props.isLoading
);
const containerClasses = computed(() => [BASE_CLASS]);

const SUPPORTED_COMPONENTS = {
  ...NATIVE_COMPONENTS,
  SPicture: defineAsyncComponent(() => import('@/components/molecules/SPicture.vue')),
  SButton: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
  SFaq: defineAsyncComponent(() => import('@/components/molecules/SFaq.vue')),
  SHeadline: defineAsyncComponent(() => import('@/components/atoms/SHeadline.vue')),
  SList: defineAsyncComponent(() => import('@/components/atoms/SList.vue')),
  STile: defineAsyncComponent(() => import('@/components/molecules/STile.vue')),
  SContent: defineAsyncComponent(() => import('@/components/atoms/SContent.vue')),
  SVideoEmbed: defineAsyncComponent(() => import('@/components/atoms/SVideoEmbed.vue')),
  SHero: defineAsyncComponent(() => import('@/components/molecules/heroes/SHero.vue')),
  SHtml: defineAsyncComponent(() => import('@/components/atoms/SHtml.vue')),
  SEventGrid: defineAsyncComponent(() => import('@/components/Product/SEventGrid.vue')),
  STabbedEventGrid: defineAsyncComponent(() => import('@/components/Pages/ProductListing/STabbedEventGrid.vue')),
  SFilteredEventGrid: defineAsyncComponent(() => import('@/components/Pages/ProductListing/SFilteredEventGrid.vue')),
  SNewsletterBanner: defineAsyncComponent(() => import('@/components/organisms/newsletter/SNewsLetterBanner.vue')),
  SLogoRow: defineAsyncComponent(() => import('@/components/molecules/SLogoRow.vue')),
  SSeparator: defineAsyncComponent(() => import('@/components/atoms/SSeparator.vue')),
  SAnchor: defineAsyncComponent(() => import('@/components/atoms/SAnchor.vue')),
  WebpImage: defineAsyncComponent(() => import('@/components/atoms/WebpImage.vue')),
  NSocialLinks: defineAsyncComponent(() => import('@/components/atoms/SocialLinks.vue')),
  SHeaderSlogan: defineAsyncComponent(() => import('@/components/Header/SHeaderSlogan.vue')),
  SCarousel: defineAsyncComponent(() => import('@/components/molecules/carousels/SCarousel.vue')),
  Slogan: defineAsyncComponent(() => import('@/components/atoms/SSlogan.vue')),
  SReviews: defineAsyncComponent(() => import('@/components/molecules/SReviews.vue')),
  SVoucherHeader: defineAsyncComponent(() => import('@/components/molecules/heroes/SVoucherHeader.vue')),
  NuxtTransformImage: defineAsyncComponent(() => import('@/components/molecules/NuxtTransformImage.vue')),
  SBannerContent: defineAsyncComponent(() => import('@/components/molecules/SBannerContent.vue')),
  SGeneralBanner: defineAsyncComponent(() => import('@/components/molecules/SGeneralBanner.vue')),
  SDiscoverBanner: defineAsyncComponent(() => import('@/components/molecules/SDiscoverBanner.vue')),
  SHomepageHero: defineAsyncComponent(() => import('@/components/molecules/heroes/SHomepageHero.vue')),
  SFormattedText: defineAsyncComponent(() => import('@/components/atoms/SFormattedText.vue')),
  HomepageCitySelector: defineAsyncComponent(() => import('@/components/molecules/selects/HomepageCitySelector.vue')),
  SArtnightUSPs: defineAsyncComponent(() => import('@/components/molecules/SArtnightUsps.vue')),
  SUspCard: defineAsyncComponent(() => import('@/components/atoms/SUspCard.vue')),
  SFounder: defineAsyncComponent(() => import('@/components/molecules/SFounder.vue')),
  SPrivateArtnight: defineAsyncComponent(() => import('@/components/molecules/SPrivateArtnight.vue')),
  SHowItWorks: defineAsyncComponent(() => import('@/components/molecules/SHowItWorks.vue'))
};

function scrollToTop() {
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, 200);
}

onMounted(() => {
  setTimeout(() => {
    if (location?.hash) {
      const hash = location.hash.replace('#', '');
      const el = document.getElementsByName(hash)?.[0];
      if (el) el.scrollIntoView({});
    } else {
      scrollToTop();
    }
  }, 1000);
});
</script>

<style lang="scss">
.flex-layout {
  color: var(--color-night-blue-900);
  flex-grow: 1;
  br + br {
    display: none;
  }
  .rich-text-link {
    text-decoration: underline;
    text-decoration-color: rgba(68, 68, 68, 0.6);
    text-underline-position: under;
  }
}
</style>
